// @flow

import * as React from 'react';
import { FormControl } from '@material-ui/core';
import { OutlinedInput, List, ListItem } from '@material-ui/core';
import { useCombobox } from 'downshift';
import { subYears, format } from 'date-fns';
import { useLocale } from '../hooks/locale.js';
import { InputPopup } from './popup.js';

type Props = {|
  disabled?: boolean,
  // used only to select years, doesn't really limits output
  minDate?: Date,
  maxDate?: Date,
  value: ?string,
  onChange: string => void,
|};

const filterItems = (items, inputValue) => {
  const lowercased = inputValue.toLowerCase();
  if (inputValue === '') {
    return items;
  }
  return items.filter(
    item => item.label != null && item.label.toLowerCase().includes(lowercased),
  );
};

// apply selected item label when input looses focus
const reduceSelectState = (state, action) => {
  switch (action.type) {
    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...action.changes,
        inputValue: action.props.itemToString?.(action.changes.selectedItem),
      };
    default:
      return action.changes;
  }
};

// optimise rendering big lists like years
const SelectItem = React.memo(({ getItemProps, index, item, selected }) => {
  return (
    <ListItem {...getItemProps({ index, item, button: true, selected })}>
      {item.label}
    </ListItem>
  );
});

const SelectInput = ({
  disabled,
  placeholder,
  inputMode,
  inputRef,
  items,
  value,
  onSelect,
}) => {
  const [filtered, dispatchFilter] = React.useReducer(
    (state, inputValue) => filterItems(items, inputValue),
    items,
  );
  const wrapperRef = React.useRef(null);
  const combobox = useCombobox({
    items: filtered,
    stateReducer: reduceSelectState,
    // make selectedItem always controlled by coalescing null
    selectedItem: items.find(item => item.id === value) ?? null,
    // select first matched case on search
    defaultHighlightedIndex: 0,
    itemToString: item => item?.label ?? '',
    onInputValueChange: ({ inputValue }) => dispatchFilter(inputValue),
    onSelectedItemChange: ({ selectedItem }) => onSelect(selectedItem?.id),
  });
  const { getComboboxProps } = combobox;
  const { getInputProps, getMenuProps, getItemProps } = combobox;
  const { inputValue, isOpen, highlightedIndex } = combobox;
  const { openMenu } = combobox;
  return (
    <div {...getComboboxProps()}>
      <FormControl>
        <OutlinedInput
          {...getInputProps({
            ref: wrapperRef,
            disabled,
            placeholder,
            inputMode,
            inputProps: {
              ref: inputRef,
            },
            onFocus: () => {
              if (isOpen === false) {
                dispatchFilter(inputValue);
                openMenu();
              }
            },
          })}
        />
      </FormControl>
      <InputPopup referenceRef={wrapperRef} open={isOpen}>
        <List {...getMenuProps()}>
          {isOpen &&
            filtered.map((item, index) => (
              <SelectItem
                key={item.id}
                getItemProps={getItemProps}
                index={index}
                item={item}
                selected={highlightedIndex === index}
              />
            ))}
        </List>
      </InputPopup>
    </div>
  );
};

function range(from: number, to: number): Array<number> {
  return Array.from({ length: Math.abs(to - from) + 1 }, (_, i) =>
    to >= from ? i + from : from - i,
  );
}

function daysInMonth(year: ?number, month: ?number): number {
  if (month == null || year == null) {
    return 30;
  }
  return new Date(year, month + 1, 0).getDate();
}

function fixDay(year: ?number, month: ?number, day: ?number): ?number {
  if (day == null) {
    return day;
  }
  return daysInMonth(year, month) < day ? 1 : day;
}

type State = {|
  year: ?number,
  month: ?number,
  day: ?number,
|};

const setYear = (state, year) => ({
  year,
  month: state.month,
  day: fixDay(year, state.month, state.day),
});

const setMonth = (state, month) => ({
  year: state.year,
  month,
  day: fixDay(state.year, month, state.day),
});

const setDay = (state, day) => ({
  year: state.year,
  month: state.month,
  day: fixDay(state.year, state.month, day),
});

const valueToState = value => {
  if (value == null) {
    return { year: null, month: null, day: null };
  }
  const date = new Date(value);
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  };
};

export const ComboDate = (props: Props): React.Node => {
  const { dateLocale, t } = useLocale();

  const {
    value,
    onChange,
    minDate = new Date('1940-01-01'),
    maxDate = subYears(new Date(), 14),
    disabled = false,
  } = props;

  const [state, setState] = React.useState<State>(() => valueToState(value));

  React.useEffect(() => {
    setState(valueToState(value));
  }, [value]);

  const setStateAndReportChange = newState => {
    if (
      newState.year != null &&
      newState.month != null &&
      newState.day != null
    ) {
      const date = new Date(newState.year, newState.month, newState.day);
      const formatted = format(date, 'yyyy-MM-dd', { locale: dateLocale });
      onChange(formatted);
    }
    setState(newState);
  };

  const dayRef = React.useRef(null);
  const monthRef = React.useRef(null);
  const yearRef = React.useRef(null);

  // changing select items on every render
  // will trigger controlled selectedItem change
  const days = React.useMemo(() => {
    return range(1, daysInMonth(state.year, state.month)).map(id => {
      return { id, label: id.toString() };
    });
  }, [state.year, state.month]);
  const months = React.useMemo(() => {
    return range(0, 11).map(id => {
      return {
        id,
        label: format(new Date(2000, id, 1), 'MMMM', { locale: dateLocale }),
      };
    });
  }, [dateLocale]);
  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();
  const years = React.useMemo(() => {
    return range(maxYear, minYear).map(id => {
      return { id, label: id.toString() };
    });
  }, [minYear, maxYear]);

  return (
    <div
      css={{ display: 'grid', gridTemplateColumns: '64px 1fr 84px', gap: 8 }}
    >
      <SelectInput
        disabled={disabled}
        placeholder={t('formsDate')}
        inputMode="numeric"
        inputRef={dayRef}
        items={days}
        value={state.day}
        onSelect={id => {
          setStateAndReportChange(setDay(state, id));
          if (monthRef.current != null) {
            monthRef.current.focus();
          }
        }}
      />
      <SelectInput
        disabled={disabled}
        placeholder={t('formsMonth')}
        inputMode="text"
        inputRef={monthRef}
        items={months}
        value={state.month}
        onSelect={id => {
          setStateAndReportChange(setMonth(state, id));
          if (yearRef.current != null) {
            yearRef.current.focus();
          }
        }}
      />
      <SelectInput
        disabled={disabled}
        placeholder={t('formsYear')}
        inputMode="numeric"
        inputRef={yearRef}
        items={years}
        value={state.year}
        onSelect={id => {
          setStateAndReportChange(setYear(state, id));
        }}
      />
    </div>
  );
};
